import { Stack, Text, useMantineTheme, Box, Group, createStyles, Button } from '@mantine/core'
import React, { useCallback, useMemo } from 'react'
import { useLottie } from 'lottie-react'
import Fade from 'react-reveal/Fade'
import { LottieAnimations } from 'src/assets'
import { useMediaQuery } from '@mantine/hooks'
import { transitionStyles } from 'src/styles'
import ReactDOM from 'react-dom'
import { HeroCTAMenu, MenuItem } from 'src/content/links'
import { DomUtils } from 'src/utils'

export interface HeroContent2Props {}

const useStyles = createStyles((theme, { isMobile }: { isMobile: boolean }) => ({
  wavingImageStyle: {
    // transformStyle: rotate('45deg'),
    div: {
      width: 40,
      height: 40,
    },
  },
  titleText: {
    fontSize: isMobile ? 30 : 40,
    color: theme.colorScheme === 'dark' ? theme.white : theme.black,
    fontWeight: 900,
    textAlign: isMobile ? 'center' : 'left',
    lineHeight: 1.3,
  },
  subTitle: {
    fontSize: theme.fontSizes.xl,
    color: theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[7],
    textAlign: isMobile ? 'center' : 'left',
    paddingRight: isMobile ? 0 : 40,
    padding: isMobile ? 10 : 0,
  },
  ctaButton: {
    // paddingInline: isMobile ? 0 : 20,
    marginRight: isMobile ? 0 : theme.spacing.lg,
    borderRadius: theme.radius.xl,
    marginTop: theme.spacing.lg,
    '&:hover': {
      transform: 'scale(1.1)',
      transition: transitionStyles.themeChange,
    },
  },
}))

export const HeroContent2: React.FC<HeroContent2Props> = () => {
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { classes } = useStyles({ isMobile })

  const { wavingHand } = LottieAnimations.Hero

  const { View: WavingHand } = useLottie({
    animationData: wavingHand,
    loop: true,
    autoplay: true,
  })

  const greetingMessage = useMemo(() => {
    const currentHour = new Date().getHours()
    if (currentHour < 12) {
      return 'Morning'
    } else if (currentHour < 16) {
      return 'Afternoon'
    } else {
      return 'Evening'
    }
  }, [])

  const getElementByClassName = useCallback((className: string) => {
    const element = ReactDOM.findDOMNode(document.getElementsByClassName(className)[0])
    return element
  }, [])

  const scrollToSection = useCallback(
    (className: string, samePage?: boolean) => {
      const element = getElementByClassName(className) as HTMLElement
      if (element && samePage) {
        DomUtils.scrollTo(element, 'bottom', { smooth: true, defer: true })
      }
    },
    [getElementByClassName],
  )

  const navigateToSection = useCallback((href: string) => {
    window.open(href, '_blank')?.focus()
  }, [])

  const handleNavigation = useCallback(
    (menu: MenuItem) => {
      if (menu.samePage) {
        menu.sectionClassName && scrollToSection(menu.sectionClassName, menu.samePage)
      } else {
        menu.link && navigateToSection(menu.link)
      }
    },
    [scrollToSection, navigateToSection],
  )

  return (
    <Stack align={isMobile ? 'center' : 'flex-start'} justify={isMobile ? 'center' : 'flex-start'}>
      <Group>
        <Text size="xl">Good {greetingMessage}, I'm Tirth</Text>
        <Box className={classes.wavingImageStyle}>{WavingHand}</Box>
      </Group>
      <Fade left>
        <Text className={classes.titleText}>Front-end & Mobile App</Text>
        <Text className={classes.titleText} sx={{ marginTop: -16 }}>
          Full stack Developer
        </Text>
        <Text className={classes.subTitle}>
          Passionate software developer crafting innovative solutions & driving business growth through code having
          focus on user-centric product.
        </Text>
      </Fade>
      <Fade bottom>
        <Group>
          <Button
            className={classes.ctaButton}
            size={isMobile ? 'md' : 'lg'}
            variant="gradient"
            gradient={{ from: 'blue', to: 'cyan' }}
            onClick={() => handleNavigation(HeroCTAMenu[0])}
          >
            View my work
          </Button>
          <Button
            className={classes.ctaButton}
            size={isMobile ? 'md' : 'lg'}
            variant="outline"
            onClick={() => handleNavigation(HeroCTAMenu[1])}
          >
            More about me
          </Button>
        </Group>
      </Fade>
    </Stack>
  )
}
